/**
 * Wizard de import compartido (F3-J): subir → mapear (auto-match + selects) →
 * preview con issues por fila → modo (parcial/atómico) + aplicar → progreso →
 * resultados. Embebible desde cualquier pantalla:
 *
 * ```tsx
 * <ImportWizard entity={entity} orgId={activeOrgId()} open={open()} onOpenChange={setOpen} />
 * ```
 */

import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { createEffect, createMemo, createSignal, For, Match, Show, Switch } from "solid-js";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  XCircle,
} from "lucide-solid";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/components/ui/toast";
import { api, createApiQuery } from "@/lib/api";
import type { components } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import {
  downloadIoFile,
  type ImportJobResponse,
  type IoEntity,
  ioQueryKeys,
  uploadImportFile,
} from "../api";

type ErrorModel = components["schemas"]["ErrorModel"];

interface ImportWizardProps {
  entity: IoEntity;
  orgId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Notifica un apply terminado con éxito (para refrescar la pantalla host). */
  onDone?: () => void;
}

type Step = "upload" | "mapping" | "preview" | "running" | "done";

const POLL_MS = 1500;
const PREVIEW_COLUMNS_MAX = 8;

export function ImportWizard(props: ImportWizardProps) {
  const { t } = useI18n();
  const [step, setStep] = createSignal<Step>("upload");
  const [jobId, setJobId] = createSignal<string | undefined>(undefined);
  const [busy, setBusy] = createSignal(false);
  // Mapeo editable: cabecera → column_key | null (ignorar).
  const [mapping, setMapping] = createSignal<Record<string, string | null>>({});
  const [mode, setMode] = createSignal<"partial" | "atomic">("partial");
  const [sendInvites, setSendInvites] = createSignal(false);
  // Polling activo mientras el apply corre en el server (señal propia para no
  // leer la query dentro de sus propias opciones).
  const [polling, setPolling] = createSignal(false);

  const jobQuery = createApiQuery<ImportJobResponse, ErrorModel>(
    () => ioQueryKeys.job(jobId()),
    async () => api.GET("/api/io/import/{id}", { params: { path: { id: jobId() ?? "" } } }),
    () => ({
      enabled: jobId() !== undefined && props.open,
      refetchInterval: polling() ? POLL_MS : false,
    }),
  );

  const job = () => jobQuery.data?.job;
  // Stats numéricas del job sin asserts inseguros (el jsonb llega como unknown).
  const stats = createMemo<Record<string, number>>(() => {
    const raw: unknown = job()?.stats;
    const out: Record<string, number> = {};
    if (typeof raw === "object" && raw !== null) {
      for (const [k, v] of Object.entries(raw)) {
        if (typeof v === "number") {
          out[k] = v;
        }
      }
    }
    return out;
  });

  // El paso "running" termina cuando el job sale de applying.
  createEffect(() => {
    const status = job()?.status;
    if (step() === "running" && (status === "done" || status === "failed")) {
      setPolling(false);
      setStep("done");
      if (status === "done") {
        props.onDone?.();
      }
    }
  });

  // Inicializa el mapeo editable desde la propuesta de auto-match.
  createEffect(() => {
    const proposal = jobQuery.data?.proposal;
    if (proposal && Object.keys(mapping()).length === 0) {
      const initial: Record<string, string | null> = {};
      for (const c of proposal.columns) {
        initial[c.header] = c.columnKey ?? null;
      }
      setMapping(initial);
    }
  });

  const reset = () => {
    setStep("upload");
    setJobId(undefined);
    setMapping({});
    setMode("partial");
    setSendInvites(false);
    setPolling(false);
  };

  const close = (open: boolean) => {
    if (!open) {
      reset();
    }
    props.onOpenChange(open);
  };

  const onFileChosen = async (file: File | undefined) => {
    if (!file) {
      return;
    }
    setBusy(true);
    try {
      const res = await uploadImportFile(props.entity.key, props.orgId, file);
      setJobId(res.job.id);
      setStep("mapping");
    } catch (e) {
      showToast(`${t(msg`Upload failed`)}: ${e instanceof Error ? e.message : String(e)}`, "error");
    } finally {
      setBusy(false);
    }
  };

  const missingRequired = createMemo(() => {
    const assigned = new Set(Object.values(mapping()).filter(Boolean));
    return props.entity.columns.filter((c) => c.required && !assigned.has(c.key));
  });

  const confirmMapping = async () => {
    const id = jobId();
    if (!id) {
      return;
    }
    setBusy(true);
    try {
      const res = await api.PUT("/api/io/import/{id}/mapping", {
        params: { path: { id } },
        body: { columns: mapping() },
      });
      if (res.error) {
        throw new Error(JSON.stringify(res.error));
      }
      await jobQuery.refetch();
      setStep("preview");
    } catch (e) {
      showToast(
        `${t(msg`Mapping failed`)}: ${e instanceof Error ? e.message : String(e)}`,
        "error",
      );
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    const id = jobId();
    if (!id) {
      return;
    }
    setBusy(true);
    try {
      const res = await api.POST("/api/io/import/{id}/apply", {
        params: { path: { id } },
        body: { mode: mode(), sendInvites: sendInvites() },
      });
      if (res.error) {
        throw new Error(JSON.stringify(res.error));
      }
      setStep("running");
      setPolling(true);
      void jobQuery.refetch();
    } catch (e) {
      showToast(`${t(msg`Apply failed`)}: ${e instanceof Error ? e.message : String(e)}`, "error");
    } finally {
      setBusy(false);
    }
  };

  const downloadErrors = async () => {
    const id = jobId();
    if (!id) {
      return;
    }
    try {
      await downloadIoFile(`/api/io/import/${id}/issues.xlsx`, "errores-import.xlsx");
    } catch (e) {
      showToast(
        `${t(msg`Download failed`)}: ${e instanceof Error ? e.message : String(e)}`,
        "error",
      );
    }
  };

  const columnOptions = createMemo(() => [
    { value: null as string | null, label: t(msg`Ignore column`) },
    ...props.entity.columns.map((c) => ({
      value: c.key as string | null,
      label: c.required ? `${c.labelI18n} *` : c.labelI18n,
    })),
  ]);

  const previewColumns = createMemo(() => props.entity.columns.slice(0, PREVIEW_COLUMNS_MAX));

  return (
    <Dialog open={props.open} onOpenChange={close} role="dialog" contentClass="max-w-3xl">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2">
          <FileSpreadsheet class="h-5 w-5 text-primary" />
          {t(msg`Import`)} · {props.entity.nameI18n}
        </DialogTitle>
      </DialogHeader>

      <div class="space-y-4 p-1" data-testid="io-import-wizard">
        <StepBar step={step()} />

        <Switch>
          {/* ── Paso 1: subir ── */}
          <Match when={step() === "upload"}>
            <div class="space-y-3">
              <p class="text-sm text-muted-foreground">
                {t(
                  msg`Upload an .xlsx or .csv file. The first row must contain the column headers.`,
                )}
              </p>
              <div class="rounded-xl border border-dashed border-input p-6 text-center">
                <input
                  type="file"
                  accept=".xlsx,.csv"
                  disabled={busy()}
                  data-testid="io-import-file"
                  onChange={(e) => void onFileChosen(e.currentTarget.files?.[0])}
                />
              </div>
              <Show when={busy()}>
                <div class="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 class="h-4 w-4 animate-spin" /> {t(msg`Uploading and parsing…`)}
                </div>
              </Show>
              <div class="text-xs text-muted-foreground">
                {t(msg`Expected columns`)}:{" "}
                {props.entity.columns
                  .map((c) => (c.required ? `${c.labelI18n}*` : c.labelI18n))
                  .join(", ")}
              </div>
            </div>
          </Match>

          {/* ── Paso 2: mapear columnas ── */}
          <Match when={step() === "mapping"}>
            <Show when={jobQuery.data?.proposal} fallback={<Skeleton class="h-40 w-full" />}>
              {(proposal) => (
                <div class="space-y-3">
                  <p class="text-sm text-muted-foreground">
                    {t(
                      msg`Match each file header to a destination column. Auto-matched headers are pre-selected.`,
                    )}
                  </p>
                  <div class="max-h-72 overflow-y-auto rounded-lg border border-input">
                    <table class="w-full text-sm">
                      <thead class="sticky top-0 bg-muted/60 text-xs text-muted-foreground">
                        <tr>
                          <th class="p-2 text-start">{t(msg`File header`)}</th>
                          <th class="p-2 text-start">{t(msg`Match`)}</th>
                          <th class="p-2 text-start">{t(msg`Destination column`)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <For each={proposal().columns}>
                          {(col) => (
                            <tr class="border-t border-input/60">
                              <td class="p-2 font-medium">{col.header}</td>
                              <td class="p-2">
                                <MatchBadge kind={col.matchedBy} />
                              </td>
                              <td class="p-2">
                                <Select
                                  options={columnOptions()}
                                  value={mapping()[col.header] ?? null}
                                  onChange={(v) => setMapping({ ...mapping(), [col.header]: v })}
                                  aria-label={col.header}
                                  triggerClass="h-8 min-w-40"
                                />
                              </td>
                            </tr>
                          )}
                        </For>
                      </tbody>
                    </table>
                  </div>
                  <Show when={missingRequired().length > 0}>
                    <div class="flex items-center gap-2 text-sm text-destructive">
                      <AlertTriangle class="h-4 w-4" />
                      {t(msg`Required columns not mapped`)}:{" "}
                      {missingRequired()
                        .map((c) => c.labelI18n)
                        .join(", ")}
                    </div>
                  </Show>
                  <div class="flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => close(false)}>
                      {t(msg`Cancel`)}
                    </Button>
                    <Button
                      disabled={busy() || missingRequired().length > 0}
                      onClick={() => void confirmMapping()}
                      data-testid="io-confirm-mapping"
                    >
                      <Show when={busy()}>
                        <Loader2 class="h-4 w-4 animate-spin" />
                      </Show>
                      {t(msg`Validate file`)}
                    </Button>
                  </div>
                </div>
              )}
            </Show>
          </Match>

          {/* ── Paso 3: preview + modo + aplicar ── */}
          <Match when={step() === "preview"}>
            <div class="space-y-3">
              <div class="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="io-preview-counts">
                <StatChip label={t(msg`Rows`)} value={stats().rows ?? 0} />
                <StatChip label={t(msg`Valid`)} value={stats().valid ?? 0} tone="ok" />
                <StatChip label={t(msg`Warnings`)} value={stats().warnings ?? 0} tone="warn" />
                <StatChip label={t(msg`Errors`)} value={stats().errors ?? 0} tone="err" />
              </div>

              <Show when={(jobQuery.data?.errors ?? 0) > 0 || (jobQuery.data?.warnings ?? 0) > 0}>
                <Button variant="outline" size="sm" onClick={() => void downloadErrors()}>
                  <Download class="h-4 w-4" /> {t(msg`Download error report`)}
                </Button>
              </Show>

              <div class="max-h-56 overflow-auto rounded-lg border border-input">
                <table class="w-full text-xs">
                  <thead class="sticky top-0 bg-muted/60 text-muted-foreground">
                    <tr>
                      <For each={previewColumns()}>
                        {(c) => <th class="p-2 text-start whitespace-nowrap">{c.labelI18n}</th>}
                      </For>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={jobQuery.data?.preview ?? []}>
                      {(row) => (
                        <tr class="border-t border-input/60">
                          <For each={previewColumns()}>
                            {(c) => <td class="p-2 whitespace-nowrap">{formatCell(row, c.key)}</td>}
                          </For>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </div>

              <div class="space-y-2 rounded-lg border border-input p-3">
                <div class="text-sm font-medium">{t(msg`Apply mode`)}</div>
                <label class="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="io-mode"
                    checked={mode() === "partial"}
                    onChange={() => setMode("partial")}
                  />
                  <span>
                    <span class="font-medium">{t(msg`Partial`)}</span> —{" "}
                    {t(msg`valid rows are imported; failing rows are reported.`)}
                  </span>
                </label>
                <label class="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="io-mode"
                    checked={mode() === "atomic"}
                    onChange={() => setMode("atomic")}
                  />
                  <span>
                    <span class="font-medium">{t(msg`Atomic`)}</span> —{" "}
                    {t(msg`all or nothing: any failure rolls back the whole import.`)}
                  </span>
                </label>
                <Show when={props.entity.key === "io.students"}>
                  <label class="flex items-center gap-2 text-sm">
                    <Checkbox checked={sendInvites()} onChange={setSendInvites} />
                    {t(msg`Create pending invites for new students with a real email`)}
                  </label>
                </Show>
              </div>

              <div class="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setStep("mapping")}>
                  {t(msg`Back`)}
                </Button>
                <Button
                  disabled={busy() || (stats().valid ?? 0) === 0}
                  onClick={() => void apply()}
                  data-testid="io-apply"
                >
                  <Show when={busy()}>
                    <Loader2 class="h-4 w-4 animate-spin" />
                  </Show>
                  {t(msg`Import {count} rows`, { count: stats().valid ?? 0 })}
                </Button>
              </div>
            </div>
          </Match>

          {/* ── Paso 4: progreso ── */}
          <Match when={step() === "running"}>
            <div class="space-y-3 py-6 text-center" data-testid="io-running">
              <Loader2 class="mx-auto h-8 w-8 animate-spin text-primary" />
              <div class="text-sm text-muted-foreground">
                {t(msg`Applying…`)} {stats().appliedRows ?? 0}/{stats().valid ?? 0}
              </div>
            </div>
          </Match>

          {/* ── Paso 5: resultados ── */}
          <Match when={step() === "done"}>
            <div class="space-y-3 py-4 text-center" data-testid="io-results">
              <Show
                when={job()?.status === "done"}
                fallback={
                  <>
                    <XCircle class="mx-auto h-8 w-8 text-destructive" />
                    <div class="text-sm font-medium">{t(msg`Import failed`)}</div>
                    <div class="text-xs text-muted-foreground">{job()?.error}</div>
                  </>
                }
              >
                <CheckCircle2 class="mx-auto h-8 w-8 text-primary" />
                <div class="text-sm font-medium">{t(msg`Import finished`)}</div>
              </Show>
              <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <StatChip label={t(msg`Inserted`)} value={stats().inserted ?? 0} tone="ok" />
                <StatChip label={t(msg`Updated`)} value={stats().updated ?? 0} tone="ok" />
                <StatChip label={t(msg`Skipped`)} value={stats().skipped ?? 0} />
                <StatChip label={t(msg`Failed`)} value={stats().failed ?? 0} tone="err" />
              </div>
              <Show when={(stats().failed ?? 0) > 0 || (stats().errors ?? 0) > 0}>
                <Button variant="outline" size="sm" onClick={() => void downloadErrors()}>
                  <Download class="h-4 w-4" /> {t(msg`Download error report`)}
                </Button>
              </Show>
              <div class="flex justify-center">
                <Button onClick={() => close(false)}>{t(msg`Close`)}</Button>
              </div>
            </div>
          </Match>
        </Switch>
      </div>
    </Dialog>
  );
}

// Descriptores estáticos: `t()` se llama en el JSX para que las etiquetas
// REACCIONEN al cambio de idioma (gotcha i18n:reactivity).
const STEP_LABELS: { key: Step; label: MessageDescriptor }[] = [
  { key: "upload", label: msg`Upload` },
  { key: "mapping", label: msg`Mapping` },
  { key: "preview", label: msg`Preview` },
  { key: "running", label: msg`Apply` },
  { key: "done", label: msg`Results` },
];

function StepBar(props: { step: Step }) {
  const { t } = useI18n();
  const idx = () => STEP_LABELS.findIndex((s) => s.key === props.step);
  return (
    <ol class="flex items-center gap-2 text-xs">
      <For each={STEP_LABELS}>
        {(s, i) => (
          <li
            class={`rounded-full px-2.5 py-1 ${
              i() === idx()
                ? "bg-primary text-primary-foreground"
                : i() < idx()
                  ? "bg-primary/15 text-primary"
                  : "bg-muted text-muted-foreground"
            }`}
          >
            {t(s.label)}
          </li>
        )}
      </For>
    </ol>
  );
}

function MatchBadge(props: { kind: "exact" | "normalized" | "none" }) {
  const { t } = useI18n();
  return (
    <Switch>
      <Match when={props.kind === "exact"}>
        <Badge variant="default">{t(msg`Exact`)}</Badge>
      </Match>
      <Match when={props.kind === "normalized"}>
        <Badge variant="secondary">{t(msg`Auto`)}</Badge>
      </Match>
      <Match when={props.kind === "none"}>
        <Badge variant="outline">{t(msg`No match`)}</Badge>
      </Match>
    </Switch>
  );
}

function StatChip(props: { label: string; value: number; tone?: "ok" | "warn" | "err" }) {
  const tone = () =>
    props.tone === "ok"
      ? "text-primary"
      : props.tone === "warn"
        ? "text-amber-600 dark:text-amber-400"
        : props.tone === "err"
          ? "text-destructive"
          : "text-foreground";
  return (
    <div class="rounded-lg border border-input p-2 text-center">
      <div class={`text-lg font-bold ${tone()}`}>{props.value.toLocaleString()}</div>
      <div class="text-[11px] text-muted-foreground">{props.label}</div>
    </div>
  );
}

function formatCell(row: unknown, key: string): string {
  if (typeof row !== "object" || row === null) {
    return "";
  }
  // Object.entries evita el assert `as Record<…>` (fila = JSON del server).
  const v: unknown = Object.entries(row).find(([k]) => k === key)?.[1];
  if (v === null || v === undefined) {
    return "";
  }
  if (typeof v === "string") {
    return v;
  }
  if (typeof v === "number" || typeof v === "boolean") {
    return String(v);
  }
  return JSON.stringify(v);
}
